import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

function normalizeCollapsedTableRows(value) {
  const lines = String(value || "").split(/\r?\n/);

  return lines
    .map((line) => {
      if (!line.includes("|") || !/\|\s*:?-{3,}:?\s*\|/.test(line)) {
        return line;
      }

      return line
        .replace(/([^\s|])\s+(\|[^|\n]+(?:\|[^|\n]+){1,}\|)/g, "$1\n$2")
        .replace(/\|\s+(?=\|)/g, "|\n");
    })
    .join("\n");
}

function normalizeLatexDelimitersOutsideCode(value) {
  return String(value || "")
    .split(/(```[\s\S]*?```|~~~[\s\S]*?~~~)/g)
    .map((fencedPart) => {
      if (fencedPart.startsWith("```") || fencedPart.startsWith("~~~")) {
        return fencedPart;
      }

      return fencedPart
        .split(/(`+[^`]*?`+)/g)
        .map((inlinePart) => {
          if (/^`/.test(inlinePart)) {
            return inlinePart;
          }

          return inlinePart
            .replace(/\\\[([\s\S]*?)\\\]/g, "\n\n$$\n$1\n$$\n\n")
            .replace(/\\\(([\s\S]*?)\\\)/g, "$$$1$");
        })
        .join("");
    })
    .join("");
}

function normalizeSingleDollarMathBlocksOutsideCode(value) {
  return String(value || "")
    .split(/(```[\s\S]*?```|~~~[\s\S]*?~~~)/g)
    .map((fencedPart) => {
      if (fencedPart.startsWith("```") || fencedPart.startsWith("~~~")) {
        return fencedPart;
      }

      return fencedPart.replace(
        /(^|\n)[ \t]*\$[ \t]*\n([\s\S]*?)\n[ \t]*\$[ \t]*(?=\n|$)/g,
        (match, prefix, body) => `${prefix}$$\n${body}\n$$`,
      );
    })
    .join("");
}

function normalizeContinuousOrderedLists(value) {
  const lines = String(value || "").split(/\r?\n/);
  let inOrderedList = false;
  let currentListNumber = 1;
  let currentListLength = 0;
  let nextOrderedListStart = 1;

  const closeOrderedList = () => {
    if (!inOrderedList) return;
    nextOrderedListStart = currentListNumber + currentListLength;
    inOrderedList = false;
    currentListLength = 0;
  };

  return lines
    .map((line) => {
      const headingMatch = line.trim().match(/^#{1,6}\s+/);
      if (headingMatch) {
        closeOrderedList();
        nextOrderedListStart = 1;
        return line;
      }

      const orderedMatch = line.match(/^(\s*)(\d+)([.)])(\s+.+)$/);
      if (!orderedMatch) {
        closeOrderedList();
        return line;
      }

      const [, indent, marker, punctuation, rest] = orderedMatch;
      const explicitStart = Number.parseInt(marker, 10);

      if (!inOrderedList) {
        currentListNumber =
          explicitStart === 1 && nextOrderedListStart > 1
            ? nextOrderedListStart
            : explicitStart;
        currentListLength = 0;
        inOrderedList = true;
      }

      const normalizedNumber = currentListNumber + currentListLength;
      currentListLength += 1;
      return `${indent}${normalizedNumber}${punctuation}${rest}`;
    })
    .join("\n");
}

function normalizeMarkdown(content, continuousOrderedLists) {
  const tableNormalized = normalizeCollapsedTableRows(content);
  const mathNormalized = normalizeLatexDelimitersOutsideCode(tableNormalized);
  const dollarBlockNormalized = normalizeSingleDollarMathBlocksOutsideCode(mathNormalized);
  return continuousOrderedLists
    ? normalizeContinuousOrderedLists(dollarBlockNormalized)
    : dollarBlockNormalized;
}

function remarkLooseStrong() {
  return (tree) => {
    const pushText = (nodes, value) => {
      if (value) {
        nodes.push({ type: "text", value });
      }
    };

    const pushStrongText = (activeStrong, value, trimTrailing = false) => {
      if (!value) return;
      let text = activeStrong.children.length === 0 ? value.replace(/^\s+/, "") : value;
      text = trimTrailing ? text.replace(/\s+$/, "") : text;
      if (text) {
        activeStrong.children.push({ type: "text", value: text });
      }
    };

    const normalizeChildren = (children) => {
      const nextChildren = [];
      let activeStrong = null;

      children.forEach((child) => {
        if (child.type !== "text") {
          if (activeStrong) {
            activeStrong.children.push(child);
          } else {
            nextChildren.push(child);
          }
          return;
        }

        let value = child.value;
        while (value.includes("**")) {
          const markerIndex = value.indexOf("**");
          const beforeMarker = value.slice(0, markerIndex);
          value = value.slice(markerIndex + 2);

          if (activeStrong) {
            pushStrongText(activeStrong, beforeMarker, true);
            if (activeStrong.children.length > 0) {
              nextChildren.push(activeStrong);
            }
            activeStrong = null;
          } else {
            pushText(nextChildren, beforeMarker);
            activeStrong = { type: "strong", children: [] };
            value = value.replace(/^\s+/, "");
          }
        }

        if (activeStrong) {
          pushStrongText(activeStrong, value);
        } else {
          pushText(nextChildren, value);
        }
      });

      if (activeStrong) {
        pushText(nextChildren, "**");
        nextChildren.push(...activeStrong.children);
      }

      return nextChildren;
    };

    const visitChildren = (node) => {
      if (!Array.isArray(node.children)) return;

      node.children.forEach((child) => {
        visitChildren(child);
      });
      node.children = normalizeChildren(node.children);
    };

    visitChildren(tree);
  };
}

const markdownPlugins = [remarkGfm, remarkMath, remarkLooseStrong];
const rehypePlugins = [[rehypeKatex, { strict: "warn" }]];

const paperAnalysisListItemClassName = [
  "break-words leading-relaxed marker:text-slate-400",
  "[&>p]:my-0",
  "[&>ul]:my-1 [&>ul]:space-y-0.5",
  "[&>ol]:my-1 [&>ol]:space-y-0.5",
].join(" ");

const blockComponents = {
  a({ children, href, ...props }) {
    return (
      <a
        {...props}
        href={href}
        target={href ? "_blank" : undefined}
        rel={href ? "noreferrer" : undefined}
        className="text-blue-600 underline underline-offset-2 hover:text-blue-800"
      >
        {children}
      </a>
    );
  },
  p({ children }) {
    return <p className="my-0 whitespace-pre-wrap leading-relaxed">{children}</p>;
  },
  h1({ children }) {
    return <h3 className="my-2 text-base font-semibold text-inherit">{children}</h3>;
  },
  h2({ children }) {
    return <h4 className="my-2 text-sm font-semibold text-inherit">{children}</h4>;
  },
  h3({ children }) {
    return <h4 className="my-2 text-sm font-semibold text-inherit">{children}</h4>;
  },
  ul({ children }) {
    return <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>;
  },
  ol({ children, start }) {
    return (
      <ol
        className="my-2 list-decimal space-y-1 pl-5"
        start={start}
      >
        {children}
      </ol>
    );
  },
  li({ children }) {
    return <li className="whitespace-pre-wrap leading-relaxed">{children}</li>;
  },
  table({ children }) {
    return (
      <div className="my-3 overflow-x-auto">
        <table className="min-w-full border-collapse text-left text-sm">
          {children}
        </table>
      </div>
    );
  },
  th({ children }) {
    return (
      <th className="border border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-900">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="border border-slate-200 px-3 py-2 align-top text-slate-800">
        {children}
      </td>
    );
  },
  strong({ children }) {
    return <strong className="font-semibold text-inherit">{children}</strong>;
  },
  em({ children }) {
    return <em className="italic text-inherit">{children}</em>;
  },
  code({ children, className }) {
    return (
      <code
        className={[
          className,
          "rounded bg-slate-100 px-1 py-0.5 text-[0.92em] text-slate-800",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </code>
    );
  },
};

const paperAnalysisComponents = {
  ...blockComponents,
  p({ children }) {
    return (
      <p className="my-1.5 whitespace-pre-wrap text-[13px] leading-6 text-slate-700">
        {children}
      </p>
    );
  },
  h1({ children }) {
    return (
      <h3 className="mb-1 mt-3 border-b border-slate-200 pb-1 text-[15px] font-semibold leading-6 text-slate-950 first:mt-0">
        {children}
      </h3>
    );
  },
  h2({ children }) {
    return (
      <h4 className="mb-1 mt-3 border-b border-slate-200 pb-1 text-[14px] font-semibold leading-6 text-slate-950 first:mt-0">
        {children}
      </h4>
    );
  },
  h3({ children }) {
    return (
      <h4 className="mb-1 mt-2.5 text-[13px] font-semibold leading-6 text-slate-900 first:mt-0">
        {children}
      </h4>
    );
  },
  h4({ children }) {
    return (
      <h5 className="mb-1 mt-2 text-[13px] font-semibold leading-6 text-slate-900 first:mt-0">
        {children}
      </h5>
    );
  },
  h5({ children }) {
    return (
      <h6 className="mb-1 mt-2 text-[12px] font-semibold uppercase tracking-wide text-slate-600 first:mt-0">
        {children}
      </h6>
    );
  },
  h6({ children }) {
    return (
      <h6 className="mb-1 mt-2 text-[12px] font-semibold uppercase tracking-wide text-slate-600 first:mt-0">
        {children}
      </h6>
    );
  },
  ul({ children }) {
    return <ul className="my-1.5 list-disc space-y-1 pl-5">{children}</ul>;
  },
  ol({ children, start }) {
    return (
      <ol
        className="my-1.5 list-decimal space-y-1 pl-5"
        start={start}
      >
        {children}
      </ol>
    );
  },
  li({ children }) {
    return <li className={paperAnalysisListItemClassName}>{children}</li>;
  },
  table({ children }) {
    return (
      <div className="my-3 overflow-x-auto border-y border-slate-300">
        <table className="min-w-full border-collapse text-left text-[12px] leading-5">
          {children}
        </table>
      </div>
    );
  },
  th({ children }) {
    return (
      <th className="border-b border-slate-300 bg-slate-50 px-3 py-2 font-semibold text-slate-900">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="border-b border-slate-100 px-3 py-2 align-top text-slate-700">
        {children}
      </td>
    );
  },
  strong({ children }) {
    return <strong className="font-semibold text-slate-950">{children}</strong>;
  },
  code({ children, className }) {
    return (
      <code
        className={[
          className,
          "rounded-sm bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-800",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </code>
    );
  },
};

const inlineComponents = {
  ...blockComponents,
  p({ children }) {
    return <>{children}</>;
  },
};

export default function MarkdownText({
  content,
  className = "",
  inline = false,
  continuousOrderedLists = false,
  variant = "default",
}) {
  const value = normalizeMarkdown(content, continuousOrderedLists).trim();
  if (!value) {
    return null;
  }

  const markdown = (
    <ReactMarkdown
      remarkPlugins={markdownPlugins}
      rehypePlugins={rehypePlugins}
      components={
        inline
          ? inlineComponents
          : variant === "paperAnalysis"
            ? paperAnalysisComponents
            : blockComponents
      }
    >
      {value}
    </ReactMarkdown>
  );

  return inline ? (
    <span className={className}>{markdown}</span>
  ) : (
    <div className={className}>{markdown}</div>
  );
}
